import React, { useEffect, useState } from 'react';
import { Table, Form, Stack, FloatingLabel } from 'react-bootstrap';
import { get } from '../../../services/apiController';
import DatePickerComponent from '../../../components/common/date-picker';
import { useForm } from 'react-hook-form';
import { TABLE_DEFAULT_LIMIT } from '../../../util/constant';
import PaginationInPage from '../../Pagination';
import moment from 'moment';
import Vendor from '../../../components/common/vendor';
import Branch from '../../../components/common/branch';
import { FiPrinter } from 'react-icons/fi';
import { GrDocumentPdf } from 'react-icons/gr';
import currency from 'currency.js';
import PreviewModal from './components/preview-modal';
import { errorToast } from '../../../util/toast.util';

const getQueryParams = (form) => {
  const queryParams = {};
  if (form.getValues('grnNumber')) {
    queryParams.grnNumber = form.getValues('grnNumber');
  }
  if (form.getValues('debitNoteNumber')) {
    queryParams.debitNoteNumber = form.getValues('debitNoteNumber');
  }
  if (form.getValues('startDate')) {
    queryParams.startDate = form.getValues('startDate');
  }
  if (form.getValues('endDate')) {
    queryParams.endDate = form.getValues('endDate');
  }
  if (form.getValues('vendorId')) {
    queryParams.vendorId = form.getValues('vendorId');
  }
  if (form.getValues('branchId')) {
    queryParams.branchId = form.getValues('branchId');
  }
  return queryParams;
};

export default function InvDebitNote() {
  const [notes, setNotes] = useState([]);

  const [previewDebitNote, setPreviewDebitNote] = useState({
    debitNoteId: '',
    open: false,
  });

  const form = useForm({
    defaultValues: {
      page: 1,
      totalResults: 0,
      grnNumber: '',
      debitNoteNumber: '',
      startDate: '',
      endDate: '',
      vendorId: '',
      branchId: '',
    },
  });

  const loadDebitNotes = async () => {
    try {
      const filters = new URLSearchParams(getQueryParams(form));
      const filtersString = filters.size > 0 ? `&${filters}` : '';
      const response = await get(`/inv/debit-notes?page=${form.getValues('page')}&limit=${TABLE_DEFAULT_LIMIT}${filtersString}`);
      setNotes(response.data.rows);
      form.setValue('totalResults', response.data.count);
    } catch (error) {
      errorToast(error?.response?.data?.data?.message || error?.message);
    }
  };

  const watch = form.watch(['grnNumber', 'debitNoteNumber', 'endDate', 'startDate', 'vendorId', 'branchId']);

  useEffect(() => {
    form.setValue('page', 1);
    loadDebitNotes();
  }, [watch[0], watch[1], watch[2], watch[3], watch[4], watch[5]]);

  return (
    <>
      <div className="card">
        <div className="d-flex justify-content-between align-items-center w-100 card-header white-color" style={{ padding: '1rem 1.7rem' }}>
          <div style={{ flex: 1 }}>
            <h4 className="card-title mb-0" style={{ fontSize: '16px' }}>
              Debit Notes | {form.getValues('totalResults')}
            </h4>
          </div>
          <div style={{ flex: 2 }}>
            <div className="row w-100 justify-content-end">
              <div className="col-lg-3">
                <Stack gap={2}>
                  <FloatingLabel controlId={'grnNumber'} label={'Search GRN Number'}>
                    <Form.Control type="text" placeholder={'Search GRN Number'} {...form.register('grnNumber')} />
                  </FloatingLabel>
                  <FloatingLabel controlId={'debitNoteNumber'} label={'Search Debit Note Number'}>
                    <Form.Control type="text" placeholder={'Search Debit Note Number'} {...form.register('debitNoteNumber')} />
                  </FloatingLabel>
                </Stack>
              </div>
              <div className="col-lg-3">
                <div className="d-flex justify-content-between flex-column">
                  <DatePickerComponent
                    selectedDate={form.watch('startDate')}
                    placeholderText="Debit Note start date"
                    onDateChange={(date) => {
                      form.setValue('startDate', date || '');
                    }}
                  />
                  <div className="mt-2">
                    <DatePickerComponent
                      selectedDate={form.watch('endDate')}
                      placeholderText="Debit Note end date"
                      onDateChange={(date) => form.setValue('endDate', date || '')}
                    />
                  </div>
                </div>
              </div>
              <div className="col-lg-3">
                <Stack gap={2}>
                  <Vendor
                    onSelect={(vendor) => {
                      form.setValue('vendorId', vendor?.value || '');
                    }}
                  />
                  <Branch
                    types="factory"
                    onChange={(branchId) => {
                      form.setValue('branchId', branchId);
                    }}
                  />
                </Stack>
              </div>
            </div>
          </div>
        </div>
        <div className="card-body px-0 list-table pt-0 pb-0">
          <div className="row">
            <div className="col">
              <Table responsive striped bordered>
                <thead>
                  <tr className="text-center">
                    <th rowSpan="2">#</th>
                    <th rowSpan="2">Note No</th>
                    <th rowSpan="2">Note Date</th>
                    <th rowSpan="2">GRN Number</th>
                    <th rowSpan="2">GRN Date</th>
                    <th rowSpan="2">Vendor</th>
                    <th colSpan="2">Debit Note Details</th>
                    <th rowSpan="2">Amount (With GST)</th>
                    <th rowSpan="2">Actions</th>
                  </tr>
                  <tr className="text-center">
                    <th>Item</th>
                    <th>Quantity Returned</th>
                  </tr>
                </thead>
                <tbody>
                  {notes.map((note, index) => (
                    <React.Fragment key={note.debitNoteId}>
                      <tr key={note.debitNoteId} className="text-center">
                        <td rowSpan={note.items.length}>{index + 1}</td>
                        <td rowSpan={note.items.length}>{note.debitNoteNumber}</td>
                        <td rowSpan={note.items.length}>{moment(note.debitNoteDate).format('DD/MM/YYYY')}</td>
                        <td rowSpan={note.items.length}>{note.grnNumber}</td>
                        <td rowSpan={note.items.length}>{moment(note.grnDate).format('DD/MM/YYYY')}</td>
                        <td rowSpan={note.items.length}>{note.vendorName}</td>
                        <td>{note.items[0].itemName}</td>
                        <td>{note.items[0].quantityReturning}</td>
                        <td rowSpan={note.items.length}>{currency(note.amount).format({ symbol: '₹' })}</td>
                        <td rowSpan={note.items.length} style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                          <FiPrinter
                            size={20}
                            className="cursor-pointer"
                            onClick={() => setPreviewDebitNote({ open: true, debitNoteId: note.debitNoteId })}
                            title="Preview"
                            style={{ marginRight: '10px' }}
                          />
                          <a href={`#/inv/debit-note/print/${note.debitNoteId}`} target="_blank" rel="noreferrer">
                            <GrDocumentPdf className="react-icons red" title="Print view" />
                          </a>
                        </td>
                      </tr>
                      {note.items.length > 1 &&
                        note.items.slice(1).map((item) => (
                          <tr key={`${note.debitNoteId}-${item.itemId}`} className="text-center">
                            <td>{item.itemName}</td>
                            <td>{item.quantityReturning}</td>
                          </tr>
                        ))}
                    </React.Fragment>
                  ))}
                  {notes.length === 0 && (
                    <tr>
                      <td colSpan={10} className="text-center">
                        No records found
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              {notes.length > 0 && form.getValues('totalResults') > TABLE_DEFAULT_LIMIT && (
                <div className="d-flex justify-content-end">
                  <PaginationInPage
                    currentPage={form.getValues('page')}
                    setCurrentPage={(page) => {
                      form.setValue('page', page);
                      loadDebitNotes();
                    }}
                    totalRecords={form.getValues('totalResults')}
                    TABLE_DEFAULT_LIMIT={TABLE_DEFAULT_LIMIT}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <PreviewModal
        open={previewDebitNote.open}
        onClose={() => setPreviewDebitNote({ ...previewDebitNote, open: false })}
        debitNoteId={previewDebitNote.debitNoteId}
      />
    </>
  );
}
